/** Rules Engine v1 — shared validation contract and domain types. */

export type RulesDomain = 'driver' | 'vehicle_assignment';
export type RulesSeverity = 'hard' | 'soft';
export type RulesCategory = 'operator_internal' | 'regulatory_external';
export type OverallStatus = 'pass' | 'fail' | 'warn';
export type PresetId = 'rta' | 'gb' | 'eu' | 'manual';

export interface ValidationResultItem {
  ruleId: string;
  ruleSetId: string;
  domain: RulesDomain;
  category: RulesCategory;
  severity: RulesSeverity;
  status: 'pass' | 'fail';
  message: string;
  actual?: string | number;
  limit?: string | number;
}

export interface ValidationOverride {
  ruleId: string;
  reason: string;
  userId: string;
  at: string;
}

export interface ValidationResultV1 {
  contractVersion: 'validationResult.v1';
  overallStatus: OverallStatus;
  results: ValidationResultItem[];
  overrides?: ValidationOverride[];
}

export interface RulesDriver {
  id: string;
  customer_id: string;
  display_name: string;
  external_driver_id?: string | null;
  status: 'active' | 'inactive';
  created_at: string;
}

export interface DutyBreak {
  start_at: string;
  end_at: string;
}

export interface RulesDuty {
  id: string;
  customer_id: string;
  driver_id?: string | null;
  vehicle_id?: string | null;
  start_at: string;
  end_at: string;
  breaks: DutyBreak[];
  required_vehicle_tags: string[];
  route_ref?: string | null;
  status: 'active' | 'cancelled';
  timezone: string;
  created_at: string;
}

export interface RuleSetting {
  enabled: boolean;
  value: number | string | null;
  severity: RulesSeverity;
}

export interface RuleSet {
  id: string;
  customer_id: string;
  name: string;
  source_preset: PresetId | string;
  category: RulesCategory | string;
  settings: Record<string, RuleSetting>;
  created_at: string;
  disclaimer: string;
}

export interface RulesCustomerSettings {
  customer_id: string;
  timezone: string;
  active_driver_rule_set_id?: string | null;
  tag_vocabulary: string[];
}

export interface RulesPreset {
  id: PresetId;
  name: string;
  description: string;
  settings: Record<string, RuleSetting>;
  disclaimer: string;
  catalog: Array<{ key: string; label: string; unit: string; evaluateInV1: boolean }>;
}

export type RosteringObjectiveId = 'balanced' | 'min_vehicles' | 'min_drivers';

export const ROSTERING_OBJECTIVE_OPTIONS: Array<{
  id: RosteringObjectiveId;
  label: string;
  description: string;
}> = [
  {
    id: 'balanced',
    label: 'Balanced',
    description: 'Cover demand first, then spread work across vehicles and drivers.',
  },
  {
    id: 'min_vehicles',
    label: 'Least vehicles',
    description: 'Cover demand first, then use as few distinct vehicles as possible.',
  },
  {
    id: 'min_drivers',
    label: 'Least drivers',
    description: 'Cover demand first, then use as few distinct drivers as possible.',
  },
];
export interface RosteringRouteSnapshot {
  route_id: string;
  route_name?: string;
  start_time?: string;
  end_time?: string;
  required_vehicle_tags?: string[];
  trip_start_minutes?: number[];
  trip_duration_minutes?: number;
  service_days?: Record<string, boolean>;
}

export interface RosteringDemandBlock {
  demand_id: string;
  route_id: string;
  route_name?: string;
  start_at: string;
  end_at: string;
  required_vehicle_tags: string[];
  source: 'trip_pack' | 'operating_window';
}

export interface RosteringProposalAssignment {
  demand_id: string;
  driver_id?: string | null;
  vehicle_id?: string | null;
  route_ref?: string | null;
  start_at: string;
  end_at: string;
  required_vehicle_tags: string[];
  validation: ValidationResultV1;
}

export interface RosteringUncoveredDemand {
  demand_id: string;
  reason: string;
}

export interface RosteringProposal {
  service_date: string;
  timezone: string;
  objective?: RosteringObjectiveId;
  demands: RosteringDemandBlock[];
  assignments: RosteringProposalAssignment[];
  uncovered: RosteringUncoveredDemand[];
  stats: {
    total_demands: number;
    proposed: number;
    uncovered: number;
    pass_count: number;
    warn_count: number;
    fail_count: number;
    unique_vehicles?: number;
    unique_drivers?: number;
  };
}

export interface RosteringAcceptResult {
  demand_id: string;
  status: 'saved' | 'blocked' | 'error';
  duty_id?: string | null;
  validation?: ValidationResultV1;
  message?: string | null;
}

export function aggregateOverallStatus(results: ValidationResultItem[]): OverallStatus {
  const hardFail = results.some((r) => r.status === 'fail' && r.severity === 'hard');
  const softFail = results.some((r) => r.status === 'fail' && r.severity === 'soft');
  if (hardFail) return 'fail';
  if (softFail) return 'warn';
  return 'pass';
}
